/**
 * The lane reactions float up, on the right edge of the picture: the Watch
 * page's while live and on the replay, and the studio's over the host's
 * preview. Each page's stylesheet gives the lane and its floats (`.rx-f`)
 * their size, rise and fade, and a still fade for reduced motion; a float
 * waits `--delay` before it starts.
 */
import { type Float, type FloatLook, floatsFor, REACTION_OF } from "./reactions";
import type { ReactionCounts, ReactionKind } from "../../../shared/live-reactions";

/** How long each look is on screen, as the stylesheets animate it. */
export const FLOAT_MS: Record<FloatLook, number> = { one: 2800, burst: 3400, kin: 2600, echo: 3200 };

export class Lane {
  private timers = new Set<number>();

  constructor(
    private readonly box: HTMLElement,
    /** The most on screen at once; the oldest goes to make room. */
    private readonly cap: number,
  ) {}

  float(f: Float): void {
    while (this.box.childElementCount >= this.cap) this.box.firstElementChild?.remove();
    const el = document.createElement("span");
    el.className = f.look === "one" ? "rx-f" : `rx-f rx-${f.look}`;
    const jitter = f.still ? 0 : (Math.random() - 0.5) * 30;
    el.style.left = `${50 + f.shift + jitter}%`;
    el.style.setProperty("--dx", `${Math.round((Math.random() - 0.5) * 24)}px`);
    el.style.setProperty("--y", `${Math.round(Math.random() * 60)}%`);
    // A variable, not the inline delay: reduced motion swaps the animation and must keep the delay.
    if (f.delayMs) el.style.setProperty("--delay", `${f.delayMs}ms`);
    const face = document.createElement("span");
    face.textContent = REACTION_OF[f.kind].emoji;
    el.append(face);
    if (f.count) {
      const n = document.createElement("b");
      n.textContent = String(f.count);
      el.append(n);
    }
    this.box.append(el);
    // A timer, not `animationend`: with reduced motion or a hidden tab the animation may never run.
    const gone = window.setTimeout(() => {
      el.remove();
      this.timers.delete(gone);
    }, f.delayMs + FLOAT_MS[f.look]);
    this.timers.add(gone);
  }

  /** Your own tap: it rises at once, before the room has heard it. */
  mine(kind: ReactionKind): void {
    this.float({ kind, look: "one", delayMs: 0, shift: 0 });
  }

  /** One second of the room's totals. */
  second(counts: ReactionCounts, perSec: number): void {
    for (const f of floatsFor(counts, perSec)) this.float(f);
  }

  clear(): void {
    for (const t of this.timers) window.clearTimeout(t);
    this.timers.clear();
    this.box.replaceChildren();
  }
}
